"use client";

import { useState, type FormEvent } from "react";
import { useRouter } from "next/navigation";
import Image from "next/image";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { getFirstAccessibleRoute } from "@/lib/nav";

export default function LoginPage() {
  const router = useRouter();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ username: username.trim().toLowerCase(), password }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || "Invalid credentials");
        return;
      }

      // One-time / admin-reset password: force a password change before the
      // user can reach the dashboard.
      if (data.mustChangePassword) {
        router.push("/reset-password");
        return;
      }

      const landing = getFirstAccessibleRoute(data.role, data.permissions ?? []);
      router.push(landing);
    } catch {
      setError("An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--background)] px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4">
            <Image
              src="/praxis-logo-dark.png"
              alt="Praxis"
              width={600}
              height={129}
              className="h-10 w-auto [[data-theme=dark]_&]:hidden"
              priority
            />
            <Image
              src="/praxis-logo-light.png"
              alt="Praxis"
              width={600}
              height={129}
              className="hidden h-10 w-auto [[data-theme=dark]_&]:block"
              priority
            />
          </div>
          <CardTitle className="text-2xl">Pulse</CardTitle>
          <p className="mt-2 text-sm text-[var(--text-muted)]">
            Sign in to your account
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="rounded-md bg-[var(--bad-bg)] p-3 text-sm text-[var(--bad-text-strong)]">
                {error}
              </div>
            )}
            <Input
              id="username"
              label="Username or email"
              type="text"
              placeholder="Enter your username or email"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoFocus
            />
            <Input
              id="password"
              label="Password"
              type="password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Signing in..." : "Sign in"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
